import type { GenericObjectType } from '@rjsf/utils';
import { get, has } from 'es-toolkit/compat';
import type { TrafficCampaignFlavor } from 'generated/graphql';
import * as jsondiffpatch from 'jsondiffpatch';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-final-form';
import type { DMCCOverrideMap } from './utils';
import {
  arrayPathToFormPath,
  checkForDeletion,
  evaluateEffectiveSharingMode,
  extractChangedPathsAsArrays,
  getSharingConfig,
  getTargetActiveFlavorIndexes,
  resolveStructurePath,
  SHARING_MODE,
  trimTrailingArrayIndex,
} from './utils';
import { processStructurePaths } from './utils/array-wildcard';

const differ = jsondiffpatch.create();

/**
 * Hook for managing variable sharing between campaign flavors with DMCC user override support.
 *
 * This hook supports:
 * - Legacy forced sharing from UI schema configuration
 * - DMCC user-configured sharing mode overrides (Tier 1 in BRD algorithm)
 * - Dynamic sharing mode evaluation based on user preferences
 *
 * ## Sharing Priority (mirrors BRD algorithm):
 * 1. **Tier 0 (Forced)**: UI schema `forcedSharingMode` - highest priority
 * 2. **Tier 1 (User Override)**: DMCC `dmccOverrideMap` user preferences
 * 3. **No propagation**: If neither forced nor user override exists
 *
 * ## Usage Context
 * - Used in DMCC campaign editing scenarios where content changes should propagate
 * - Supports both legacy forced sharing and modern user-configurable sharing
 * - Assumes the current flavor is being edited (requires currentFlavorIndex)
 * - Automatically applies changes to other active flavors based on sharing configuration
 *
 * @param params - Configuration object for variable sharing
 * @param params.currentFlavorIndex - Index of the flavor currently being edited
 * @param params.uiSchema - UI schema containing field sharing configurations
 * @param params.flavors - Array of all campaign flavors (active and inactive)
 * @returns Object containing the applyVariableSharingPropagation function
 */
export function useVariableSharingPropagation({
  currentFlavorIndex,
  uiSchema,
  flavors,
}: {
  currentFlavorIndex: number;
  uiSchema: GenericObjectType;
  flavors: TrafficCampaignFlavor[];
}) {
  const formApi = useForm();
  const currentFlavor = useMemo(
    () => flavors[currentFlavorIndex],
    [
      flavors[currentFlavorIndex],
      currentFlavorIndex,
    ],
  );

  const applyVariableSharingPropagation = useCallback(
    ({
      previousFormData,
      newFormData,
      dmccOverrideMap,
    }: {
      previousFormData: unknown;
      newFormData: unknown;
      dmccOverrideMap?: DMCCOverrideMap;
    }) => {
      // Early return if no current flavor
      if (!currentFlavor) return;

      // Use the explicitly passed previousFormData instead of formData from props
      const delta = differ.diff(previousFormData, newFormData);

      // No changes detected
      if (!delta) return;

      // Extract changed field paths as arrays to handle property names with dots
      const changedPathArrays = extractChangedPathsAsArrays(delta as GenericObjectType);

      // No valid paths found
      if (changedPathArrays.length === 0) return;

      const formChanges: {
        path: string;
        value: unknown;
      }[] = [];

      // Process each changed field
      for (const fieldPathArray of changedPathArrays) {
        const sharingConfig = getSharingConfig(fieldPathArray, uiSchema);

        // Use enhanced BRD evaluation utility to determine effective sharing mode(s)
        const effectiveSharingMode = evaluateEffectiveSharingMode({
          dmccOverrideMap,
          fieldPathArray,
          sharingConfig,
        });

        // Apply propagation if we have an effective sharing mode and the path exists
        if (effectiveSharingMode) {
          // Check if the path exists in the object to differentiate between:
          // 1. Path exists, value is undefined (should share)
          // 2. Path doesn't exist (should not share)
          // 3. Array value was deleted so path does not exist (should share)
          const pathExists = has(newFormData, fieldPathArray);
          const arrayValueWasDeleted = checkForDeletion(fieldPathArray);
          const shouldPropagate = pathExists || arrayValueWasDeleted;
          if (!shouldPropagate) continue;

          const value = get(newFormData, fieldPathArray);
          const targetFlavorIndexes = getTargetActiveFlavorIndexes({
            allFlavors: flavors,
            currentFlavor,
            currentFlavorIndex,
            sharingMode: effectiveSharingMode,
          });

          if (pathExists) {
            formChanges.push(
              ...targetFlavorIndexes.map((index) => ({
                path: `flavors[${index}].content${arrayPathToFormPath(fieldPathArray)}`,
                value,
              })),
            );
          }

          // Handle structure sharing mode - check the original config, not the converted mode
          const propagateStructure = sharingConfig?.forcedSharingMode === SHARING_MODE.STRUCTURE && sharingConfig?.structurePath;
          let structurePaths: PropertyKey[][] = [];
          if (propagateStructure) {
            const structurePath = sharingConfig.structurePath;
            structurePaths = Array.isArray(structurePath)
              ? structurePath.map((path) => path.split('.'))
              : [
                  resolveStructurePath(fieldPathArray, structurePath),
                ];
          }

          // If a value was deleted from an array, we need to trim the trailing array index to get the correct path
          // to the field so that we can propagate the entire field's value
          if (arrayValueWasDeleted) {
            structurePaths = [
              trimTrailingArrayIndex(fieldPathArray),
            ];
          }

          if (arrayValueWasDeleted || propagateStructure) {
            formChanges.push(
              ...targetFlavorIndexes.flatMap((targetFlavorIndex) =>
                processStructurePaths({
                  sourceData: newFormData,
                  structurePaths,
                  targetFlavorIndex,
                }),
              ),
            );
          }
        }
      }

      // Apply all form changes in batch
      if (formChanges.length > 0) formApi.batch(() => formChanges.forEach(({ path, value }) => formApi.change(path, value)));
    },
    [
      currentFlavor,
      currentFlavorIndex,
      flavors,
      formApi,
      uiSchema,
    ],
  );

  return {
    applyVariableSharingPropagation,
  };
}
